/**
 * DashboardModalStack — every modal that the LeagueDashboard mounts at the
 * bottom of its tree. Extracted as Slice 4 of the refactor (iter63z'''''''''''').
 *
 * Pure composition layer. No state, no API calls. Every `open` flag and
 * mutation handler flows in via props. Visible modals depend on their own
 * `open` flag — the page never short-circuits the mount, so React keeps
 * the modal's internal animation/transition state consistent between
 * open/close cycles.
 *
 * The PowerPickModal's inline `mode/flat/effective/quartilePreview` compute
 * lives here (not the page) because it's purely a derivation from props
 * the modal needs — keeps the page free of one-shot helpers.
 */
import { useMemo } from "react";
import KnockoutPickModal from "@/components/dashboard/KnockoutPickModal";
import PowerPickModal from "@/components/dashboard/PowerPickModal";
import RankTeamsModal from "@/components/RankTeamsModal";
import YourTinyCrewModal from "@/components/YourTinyCrewModal";
import CrewIdentityModal from "@/components/CrewIdentityModal";
import UserGuideModal from "@/components/UserGuideModal";
import RulesModal from "@/components/RulesModal";
import PrivacyModal from "@/components/PrivacyModal";
import TermsModal from "@/components/TermsModal";
import { AddEmailModal, LogoutConfirmModal } from "@/components/dashboard/DashboardModals";
// iter87.5 — shared Live / Open / Locked status for chip → modal parity.
import { todoStatusFor, isTournamentComplete } from "@/lib/todoStatus";

export default function DashboardModalStack({
    // shared data
    leagueId,
    league,
    tournament,
    tournamentCount,
    me,
    myRow,
    myTeams,
    myParticipant,
    board,
    availableTeams,
    data,
    // open flags
    rulesOpen,
    crewModalOpen,
    crewIdentityOpen,
    userGuideOpen,
    knockoutPickModal,
    powerPickModal,
    emailModalOpen,
    logoutConfirmOpen,
    rankTeamsModal,
    legalPrivacyOpen,
    legalTermsOpen,
    // setters
    setRulesOpen,
    setCrewModalOpen,
    setCrewIdentityOpen,
    onIdentityUpdated,
    setUserGuideOpen,
    setKnockoutPickModal,
    setPowerPickModal,
    setEmailModalOpen,
    setLogoutConfirmOpen,
    setRankTeamsModal,
    setLegalPrivacyOpen,
    setLegalTermsOpen,
    setEmailDraft,
    // handlers
    onSetKnockoutPick,
    onSetPowerPick,
    onSaveEmail,
    onToggleNotifications,
    onConfirmLogout,
}) {
    // PowerPickModal inputs derived from board/tournament/me. Stable across renders.
    const powerPickInputs = useMemo(() => {
        if (!powerPickModal || !tournament || !me) return null;
        const drafted = myTeams || [];
        const mode = tournament.power_pick_mode;
        // iter87.212.32 — Pot Pick'em only: a Pot-1 ("title contenders")
        // club gives ZERO multiplier bonus as the Power Pick — picking
        // one wastes the whole feature. Split those out of `eligible`
        // into their own no-bonus bucket instead of eliminated, since
        // the club is still very much alive, just not a useful pick.
        const potMode = tournament?.pickem?.mode === "pot";
        const potMults = tournament?.pickem?.power_pick_multipliers || {};
        // iter87.264 (user-reported bug) — MLB/NBA/NFL-Playoffs/CFB-Playoffs
        // pair TWO seeds per pot (e.g. MLB seed 1-2 → Pot 1, 3-4 → Pot 2,
        // 5-6 → Pot 3); they do NOT clamp seed straight to pot number like
        // soccer/NFL-season pot pick'em does. This was mirroring the wrong
        // (generic) rule and showing seed-2 MLB teams as Pot 2 instead of
        // Pot 1, seed-3/4 as Pot 3 instead of Pot 2, etc. Mirrors the
        // backend's `season_pickem.py::mlb_pot_for_team`/`nba_pot_for_team`/
        // `nfl_po_pot_for_team`/`cfb_po_pot_for_team`/`pot_for_team` exactly.
        const tpl = tournament?.template;
        const potForSeed = (seed) => {
            const s = Number(seed);
            if (!Number.isFinite(s)) return 3;
            if (tpl === "mlb_playoffs_api") {
                if (s < 1 || s > 6) return null;
                return Math.floor((s - 1) / 2) + 1;
            }
            if (tpl === "nba_playoffs_api") {
                if (s < 1 || s > 10) return null;
                if (s <= 6) return Math.floor((s - 1) / 2) + 1;
                return 4;
            }
            if (tpl === "nfl_playoffs_api") {
                if (s < 1 || s > 7) return null;
                if (s <= 2) return 1;
                if (s <= 4) return 2;
                return 3;
            }
            if (tpl === "cfb_playoffs_api") {
                if (s < 1 || s > 12) return null;
                return Math.floor((s - 1) / 4) + 1;
            }
            const cap = tpl === "nfl_season_api" ? 4 : 3;
            return Math.min(Math.max(s, 1), cap);
        };
        const multForPot = (pot) => Number(potMults?.[String(pot)] ?? (pot === 1 ? 1 : pot === 2 ? 1.5 : 2));
        const isNoBonusPot = (t) => potMode && multForPot(potForSeed(t.seed)) <= 1;
        // Attach the correctly-computed pot number so PowerPickModal
        // doesn't need its own (sport-unaware) copy of this logic.
        const withPot = (t) => (potMode ? { ...t, pot: potForSeed(t.seed) } : t);

        const eligible = drafted.filter((t) => !t.eliminated && !t.missed_playoffs && !isNoBonusPot(t)).map(withPot);
        const ineligible = drafted.filter((t) => t.eliminated || t.missed_playoffs).map(withPot);
        const noBonus = drafted.filter((t) => !t.eliminated && isNoBonusPot(t)).map(withPot);
        const flat = tournament.power_pick_flat_multiplier;
        const effective = myRow?.power_pick_multiplier;
        const multDisplay = effective != null
            ? `${effective}×`
            : mode === "pot"
                ? "1×–2× (by pot)"
                : mode === "handicap"
                    ? "1.5×–2× (handicap)"
                    : `${flat}×`;
        // Compute the player's current quartile preview for handicap mode.
        // Mirrors backend power_pick.compute_handicap_multipliers logic
        // (without tie-boundary push — once knockout starts, the locked
        // multiplier becomes authoritative).
        let quartilePreview = null;
        if (mode === "handicap" && effective == null && myRow?.rank) {
            const players = (board || []).filter((r) => r.is_player !== false);
            const n = players.length;
            if (n > 0) {
                if (n <= 4) {
                    quartilePreview = { label: "Everyone (≤4 crewmates)", mult: "1.75×" };
                } else {
                    const topCutoff = Math.max(1, Math.floor(n / 4));
                    const bottomStart = n - Math.max(1, Math.floor(n / 4));
                    const idx = myRow.rank - 1;
                    if (idx < topCutoff) quartilePreview = { label: "Top 25%", mult: "1.5×" };
                    else if (idx >= bottomStart) quartilePreview = { label: "Bottom 25%", mult: "2×" };
                    else quartilePreview = { label: "Middle 50%", mult: "1.75×" };
                }
            }
        }
        return { eligible, ineligible, noBonus, mode, flat, effective, multDisplay, quartilePreview };
    }, [powerPickModal, tournament, me, myTeams, myRow, board]);

    return (
        <>
            {rulesOpen && (
                <RulesModal onClose={() => setRulesOpen(false)} tournament={tournament} />
            )}

            <YourTinyCrewModal
                open={crewModalOpen}
                onOpenChange={setCrewModalOpen}
                board={board}
                leagueId={league?.id}
                leagueName={league?.name}
                me={me}
            />

            {/* iter84.24 — Universal Crew Identity (avatar + flair) editor.
                Mounted unconditionally so both single + multi-tournament
                Rivalries can reach the editor from the header pill. */}
            {crewIdentityOpen !== undefined && me?.id && (
                <CrewIdentityModal
                    open={!!crewIdentityOpen}
                    onOpenChange={setCrewIdentityOpen}
                    participantId={me.id}
                    currentAvatar={myParticipant?.avatar_emoji}
                    currentFlair={myParticipant?.crew_flair}
                    onSaved={(updated) => {
                        // Patch the in-memory participants list so the
                        // header pill + leaderboard reflect the new
                        // identity immediately, without a re-fetch.
                        onIdentityUpdated?.(updated);
                        // iter84.24 fix — Close the modal on save. Without
                        // this the dialog stuck open and the operator
                        // thought the save had hung.
                        setCrewIdentityOpen?.(false);
                    }}
                />
            )}

            <UserGuideModal
                open={userGuideOpen}
                onClose={() => {
                    setUserGuideOpen(false);
                    // iter31q — Remember the user has seen the guide on this device.
                    try { localStorage.setItem(`tcr_user_guide_seen_${leagueId}`, "1"); } catch (e) { /* ignore */ }
                }}
                tournamentTemplate={tournament?.template}
                tournamentCount={tournamentCount}
            />

            <KnockoutPickModal
                open={!!(knockoutPickModal && tournament)}
                teams={availableTeams}
                currentPickTeamId={me?.knockout_pick_team_id}
                currentPickTeam={
                    me?.knockout_pick_team_id
                        ? (tournament?.teams || []).find(t => t.id === me.knockout_pick_team_id)
                        : null
                }
                status={tournament ? todoStatusFor("champion", { tournament, me, board }) : null}
                tournamentComplete={isTournamentComplete(tournament)}
                onPick={onSetKnockoutPick}
                onClose={() => setKnockoutPickModal(false)}
                template={tournament?.template}
                isPotPickem={tournament?.pickem?.mode === "pot"}
                championBonus={tournament?.pickem?.champions_pick_bonus || 0}
            />

            {/* iter35d — Power Pick modal */}
            <PowerPickModal
                open={!!(powerPickModal && tournament && me && powerPickInputs)}
                eligibleTeams={powerPickInputs?.eligible || []}
                ineligibleDrafted={powerPickInputs?.ineligible || []}
                noBonusDrafted={powerPickInputs?.noBonus || []}
                currentPickTeamId={tournament?.format === "golf" ? myRow?.power_pick_team_id : me?.power_pick_team_id}
                multiplierDisplay={powerPickInputs?.multDisplay}
                mode={powerPickInputs?.mode}
                flatMultiplier={powerPickInputs?.flat}
                quartilePreview={powerPickInputs?.quartilePreview}
                isLocked={tournament?.format === "golf" ? !!tournament?.golf?.r1_started : powerPickInputs?.effective != null}
                isGolfPickem={tournament?.golf?.mode === "pickem_lowest_avg"}
                isGolfDraft={tournament?.format === "golf" && tournament?.golf?.mode !== "pickem_lowest_avg"}
                isPotPickem={tournament?.pickem?.mode === "pot"}
                isSeason={(tournament?.format || "").toLowerCase() === "round_robin"}
                potMultipliers={tournament?.pickem?.power_pick_multipliers}
                golfStrokes={tournament?.golf?.scoring?.power_pick_strokes_per_round ?? 2}
                golfDraftMultiplier={tournament?.power_pick_flat_multiplier || 1.5}
                status={tournament ? todoStatusFor("power", { tournament, me, board }) : null}
                onPick={onSetPowerPick}
                onClose={() => setPowerPickModal(false)}
            />

            {/* Add recovery email modal */}
            <AddEmailModal
                open={emailModalOpen}
                onClose={() => setEmailModalOpen(false)}
                onSave={onSaveEmail}
                onToggleNotifications={onToggleNotifications}
                me={me}
                league={league}
            />

            {/* Logout confirm modal — warns user to save their invite code + username */}
            <LogoutConfirmModal
                open={logoutConfirmOpen}
                onClose={() => setLogoutConfirmOpen(false)}
                onConfirm={onConfirmLogout}
                league={league}
                me={me}
                onAddEmail={() => {
                    setLogoutConfirmOpen(false);
                    setEmailDraft("");
                    setEmailModalOpen(true);
                }}
            />

            {/* iter50 — Player-submitted team rankings modal. iter52c —
                lock check is per-tournament (dict only). iter87.58 —
                also lock when the tournament itself is completed. Prior
                gate only tripped on `drafts_by_tournament[tid]`, so any
                completed tournament that never actually ran a draft
                (e.g. custom formats where the operator never triggered
                one) let players re-edit rankings after the trophy was
                decided. Screenshot from operator showed exactly this
                bug on a FINAL tournament. */}
            {me?.id && (
                <RankTeamsModal
                    open={rankTeamsModal}
                    onOpenChange={setRankTeamsModal}
                    leagueId={leagueId}
                    participantId={me.id}
                    tournamentId={tournament?.id}
                    locked={
                        !!(tournament?.id && data?.league?.drafts_by_tournament?.[tournament.id])
                        || !!tournament?.completion_authorized_by
                        || ["completed", "finished"].includes((tournament?.lifecycle_state || "").toLowerCase())
                    }
                    prefillTopN={["knockout_only", "round_robin"].includes(tournament?.format) ? 5 : 0}
                />
            )}

            <PrivacyModal
                open={legalPrivacyOpen}
                onClose={() => setLegalPrivacyOpen(false)}
            />
            <TermsModal
                open={legalTermsOpen}
                onClose={() => setLegalTermsOpen(false)}
                onAccept={() => setLegalTermsOpen(false)}
                onShowPrivacy={() => {
                    setLegalTermsOpen(false);
                    setLegalPrivacyOpen(true);
                }}
            />
        </>
    );
}
